import { queryOptions } from "@tanstack/react-query";
import { DEFAULT_GEOCODE_CONFIG } from "@/types/config.types";
import { geocodeService } from "./geocode.service";
import type {
	GeocodeRequest,
	GeocodeResult,
	GeocodeSuggestRequest,
} from "./geocode.types";

export const geocodeKeys = {
	all: ["geocode"] as const,
	suggest: (params: GeocodeSuggestRequest) =>
		[...geocodeKeys.all, "suggest", params] as const,
	geocode: (magicKey: string, countryCode?: string) =>
		[...geocodeKeys.all, "geocode", magicKey, countryCode ?? "USA"] as const,
};

export interface GeocodeSuggestQueryOptionsParams {
	request: GeocodeSuggestRequest;
	enabled?: boolean;
	staleTime?: number;
	gcTime?: number;
	suggestFn?: (
		request: GeocodeSuggestRequest,
	) => ReturnType<typeof geocodeService.suggestAddress>;
}

/**
 * TanStack Query options for debounced geocode suggest requests.
 */
export function geocodeSuggestQueryOptions({
	request,
	enabled = true,
	staleTime = DEFAULT_GEOCODE_CONFIG.suggestCacheStaleTimeMs,
	gcTime = DEFAULT_GEOCODE_CONFIG.suggestCacheGcTimeMs,
	suggestFn = geocodeService.suggestAddress.bind(geocodeService),
}: GeocodeSuggestQueryOptionsParams) {
	return queryOptions({
		queryKey: geocodeKeys.suggest(request),
		queryFn: () => suggestFn(request),
		enabled,
		staleTime,
		gcTime,
		refetchOnWindowFocus: false,
		retry: 1,
	});
}

export interface GeocodeByMagicKeyQueryOptionsParams {
	magicKey: string;
	countryCode?: string;
	staleTime?: number;
	gcTime?: number;
	geocodeFn?: (
		request: GeocodeRequest,
		signal?: AbortSignal,
	) => Promise<GeocodeResult>;
}

/**
 * TanStack Query options for POST /geocode by magicKey — cached and deduped per key.
 */
export function geocodeByMagicKeyQueryOptions({
	magicKey,
	countryCode = DEFAULT_GEOCODE_CONFIG.countryCode,
	staleTime = DEFAULT_GEOCODE_CONFIG.geocodeCacheStaleTimeMs,
	gcTime = DEFAULT_GEOCODE_CONFIG.geocodeCacheGcTimeMs,
	geocodeFn = (request, signal) => geocodeService.geocode(request, { signal }),
}: GeocodeByMagicKeyQueryOptionsParams) {
	const request: GeocodeRequest = { magicKey, countryCode };

	return queryOptions({
		queryKey: geocodeKeys.geocode(magicKey, countryCode),
		queryFn: ({ signal }) => geocodeFn(request, signal),
		staleTime,
		gcTime,
		retry: 0,
		refetchOnWindowFocus: false,
	});
}
